import type { ClientStatus } from '@fos/shared/contracts/crm';
import { CLIENT_STATUS_LABELS, CLIENT_STATUS_TONES } from '@fos/shared/labels';
import { formatPLN } from '@fos/shared/money';
import { toLocalDate } from '@fos/shared/time';
import { formatDateShort, plural } from '../../lib/format';
import type { Tone } from '../../ui/Status';

/**
 * Formatowanie danych klientów w UI (Figma 05 Klienci).
 */

/**
 * Inicjały klienta w kafelku listy (Figma: „RE”, „HM”, „TW”, „SP”, „EX”, „SQ”):
 * wielkie litery pierwszego słowa (REA → RE, HelpMyCar → HM, TeckWrap → TW), inaczej pierwsze litery dwóch słów
 * (Szkółka Pietraszewski → SP), inaczej dwie pierwsze litery (Squadrive → SQ).
 */
export function clientInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0] ?? '';
  const caps = [...first].filter((ch) => /\p{Lu}/u.test(ch));
  if (caps.length >= 2) return caps.slice(0, 2).join('');
  if (words.length >= 2) return `${[...first][0] ?? ''}${[...(words[1] ?? '')][0] ?? ''}`.toLocaleUpperCase('pl-PL');
  const letters = [...first].filter((ch) => /[\p{L}\p{N}]/u.test(ch));
  return letters.slice(0, 2).join('').toLocaleUpperCase('pl-PL') || '?';
}

/** Adres strony bez protokołu i „www.” (Figma: „rea.pl”). */
export function websiteLabel(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
    return `${u.hostname.replace(/^www\./, '')}${path}`;
  } catch {
    return url.replace(/^https?:\/\//i, '').replace(/^www\./, '');
  }
}

/** Uzupełnia protokół adresu wpisanego bez niego („rea.pl” → „https://rea.pl”). */
export function normalizeUrlInput(value: string): string {
  const v = value.trim();
  if (!v) return '';
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`;
}

/** „1 aktywny”, „2 aktywne”, „5 aktywnych”; brak = „–”. */
export function activeProjectsLabel(n: number | null): string {
  if (!n) return '–';
  return `${n} ${plural(n, ['aktywny', 'aktywne', 'aktywnych'])}`;
}

export function clientStatusLabel(status: ClientStatus): string {
  return CLIENT_STATUS_LABELS[status];
}

export function clientStatusTone(status: ClientStatus): Tone {
  return CLIENT_STATUS_TONES[status];
}

/** Stawka z kodem waluty: „150 zł/h”, „40 EUR/h”. */
export function formatClientRate(rate: string, currency = 'PLN'): string {
  const n = Number(rate);
  const text = Number.isInteger(n)
    ? n.toLocaleString('pl-PL')
    : n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const unit = currency === 'PLN' ? 'zł' : currency;
  return `${text.replace(/\s/g, ' ')} ${unit}/h`;
}

/** „Do rozliczenia” w tabeli (Figma 05: „2 150 zł”, bez groszy); nic do rozliczenia = „–”. */
export function unbilledLabel(unbilled: { amount: string; currency: string } | null | undefined): string {
  if (!unbilled) return '–';
  return formatPLN(unbilled.amount, { decimals: false, currency: unbilled.currency });
}

const WEEKDAYS_SHORT = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'] as const;

function utcDay(local: string): number {
  const [y, m, d] = local.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

/**
 * „Ostatni kontakt” (Figma 05): „dziś”, „wczoraj”, w ostatnim tygodniu dzień tygodnia („pt.”), potem „2 tyg. temu”,
 * po 5 tygodniach data („3.08”). Daty w Europe/Warsaw; brak = „–”.
 */
export function lastContactLabel(value: Date | string | null | undefined, now: Date = new Date()): string {
  if (!value) return '–';
  const day = toLocalDate(value instanceof Date ? value : new Date(value));
  const diff = Math.round((utcDay(toLocalDate(now)) - utcDay(day)) / 86_400_000);
  if (diff <= 0) return 'dziś';
  if (diff === 1) return 'wczoraj';
  if (diff < 7) return WEEKDAYS_SHORT[new Date(utcDay(day)).getUTCDay()] ?? formatDateShort(day, now);
  if (diff < 35) return `${Math.floor(diff / 7)} tyg. temu`;
  return formatDateShort(day, now);
}
